// src/shared/changelog/products.ts
import type { ProductListItem } from "@redux/slices/userProfileSlice";

/**
 * Products a changelog entry can belong to, in display order.
 *
 * The ids are the wire vocabulary the Tags API already speaks (`kontrol-edge`,
 * `reflector-v2`, `reflector-v1`), so the same strings work here, in
 * `chopper-backend`, and in the entry files both repos commit. They are stable
 * and decoupled from the labels below, so a product can be renamed — or
 * re-branded per client — without rewriting history.
 *
 * Ordering is meaningful: it drives the product filter chips and the aggregated
 * product tags, cross-product ids first.
 */
export const CHANGELOG_PRODUCTS = [
  "platform",
  "platform-api",
  "kontrol-edge",
  "reflector-v2",
  "reflector-v1",
  "insight",
] as const;

export type ChangelogProduct = (typeof CHANGELOG_PRODUCTS)[number];

export interface ChangelogProductMeta {
  /** Display name. Safe to change — never used as an identifier. */
  label: string;
  /**
   * One line under the name in the What's new lockup (CTX-532), answering
   * "what is this product" for a reader who has not bought it and is meeting
   * the name for the first time.
   *
   * Required rather than optional: a lockup that shows a tagline for four
   * products and a gap for the other two reads as a rendering bug, and the
   * compiler catching a missing one is cheaper than noticing it in a modal.
   */
  tagline: string;
  /**
   * `apiName` of the `PRODUCT_CONFIG` entry a tenant must hold to see entries
   * tagged with this id.
   *
   * `null` marks the two cross-product ids every tenant sees: `platform` for
   * dashboard-wide changes, and `platform-api` for customer-API changes and
   * deprecations — which any integrator needs regardless of which products
   * they bought.
   */
  productApiName: string | null;
}

export const CHANGELOG_PRODUCT_META: Record<
  ChangelogProduct,
  ChangelogProductMeta
> = {
  platform: {
    label: "Platform",
    tagline: "Console, accounts & settings",
    productApiName: null,
  },
  "platform-api": {
    label: "Platform API",
    tagline: "The customer-facing API",
    productApiName: null,
  },
  "kontrol-edge": {
    label: "Kontrol Edge",
    tagline: "Connectivity & edge services",
    productApiName: "Kontrol Edge",
  },
  "reflector-v2": {
    label: "Reflector V2",
    tagline: "BGP reflectors & propagation",
    productApiName: "Reflector V2",
  },
  "reflector-v1": {
    label: "Reflector",
    tagline: "BGP reflectors, legacy",
    productApiName: "Reflector",
  },
  insight: {
    label: "Insight",
    tagline: "Analytics & optimization",
    productApiName: "Insight",
  },
};

/**
 * Whether entries tagged with `product` should be shown to a tenant holding
 * `productList` (`state.userProfile.ProductList`).
 *
 * Two deliberate exceptions:
 *  - `platform` / `platform-api` always pass (see `productApiName`).
 *  - An empty `productList` filters nothing. The profile loads asynchronously,
 *    and treating "not loaded yet" as "entitled to nothing" would flash an
 *    empty feed before the entitlements arrive.
 *
 * This is the only place the rule lives on the client. When the feed moves to
 * `GET /api/v2/changelog` the server owns it and this predicate is left serving
 * only the filter chips — see CTX-202.
 */
export function isChangelogProductVisible(
  product: ChangelogProduct,
  productList: ProductListItem[] | undefined
): boolean {
  const { productApiName } = CHANGELOG_PRODUCT_META[product];
  if (!productApiName) return true;
  if (!productList || productList.length === 0) return true;
  return productList.some((entry) => entry.name === productApiName);
}
